<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, watch, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import type { ExistingCompany } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            company: {
                infoUserCompanyExist:
                    "Cet utilisateur ({username}) est déjà associé à une ou plusieurs organisations.",
                infoUserCompanyConsent:
                    "Sélectionnez l'organisation à laquelle rattacher cette source.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Entreprise sélectionnée (v-model)
     */
    modelValue: ExistingCompany | null
    /**
     * Liste des entreprises disponibles
     */
    companies: ExistingCompany[]
    /**
     * Nom d'utilisateur pour le message d'info
     */
    username?: string
}

interface Emits {
    /**
     * Émis lors de la sélection d'une entreprise
     */
    (e: "update:modelValue", value: ExistingCompany | null): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    username: "",
})

const emit = defineEmits<Emits>()

// ================================
// ÉTAT
// ================================

/**
 * Entreprise initiale (première de la liste)
 */
const initialCompany = ref<ExistingCompany | null>(null)

// ================================
// WATCHERS
// ================================

/**
 * Met à jour l'entreprise initiale quand la liste change
 */
watch(
    () => props.companies,
    (list) => {
        initialCompany.value = list && list.length > 0 ? list[0] : null
        // Sélectionner la première entreprise si aucune n'est sélectionnée
        if (!props.modelValue && initialCompany.value) {
            emit("update:modelValue", initialCompany.value)
        }
    },
    { immediate: true, deep: true }
)

// ================================
// INTERACTIONS
// ================================

/**
 * Gère la sélection d'une entreprise
 * @param company - Entreprise sélectionnée
 */
function onSelectCompany(company: ExistingCompany): void {
    emit("update:modelValue", company)
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    // Sélectionner la première entreprise par défaut
    if (props.companies.length > 0 && !props.modelValue) {
        initialCompany.value = props.companies[0]
        emit("update:modelValue", initialCompany.value)
    }
})
</script>

<template>
    <div class="company-select flex flex-col gap-4">
        <!-- ================================ -->
        <!-- MESSAGE D'INFO -->
        <!-- ================================ -->
        <BaseMessage severity="info">
            <p>
                {{ t("company.infoUserCompanyExist", { username }) }}
            </p>
        </BaseMessage>

        <p class="text-sm text-gray-600">
            {{ t("company.infoUserCompanyConsent") }}
        </p>

        <!-- ================================ -->
        <!-- LISTE DES ENTREPRISES EXISTANTES -->
        <!-- ================================ -->
        <div class="flex flex-col gap-2">
            <div
                v-for="company in companies"
                :key="company._id"
                class="w-full p-3 rounded-lg border transition-colors cursor-pointer"
                :class="
                    company._id === modelValue?._id
                        ? 'bg-primary-50 border-primary-400'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                "
                @click="onSelectCompany(company)"
            >
                <div class="flex items-center gap-3">
                    <BaseRadioButton
                        :input-id="`company-${company._id}`"
                        :value="company._id"
                        :model-value="modelValue?._id"
                        @update:model-value="onSelectCompany(company)"
                    />
                    <ItemCompany :company="company" />
                </div>
            </div>
        </div>
    </div>
</template>
